import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Phone, Mail } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TOPICS } from '@/topicContent';
import schemas from '@/topicSchemas.json';
import '@/Sections.css';

const SITE = 'https://www.maisblau.video';

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const MdLink = ({ href, children }) => {
  if (href && href.startsWith('/')) {
    return <Link to={href}>{children}</Link>;
  }
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
};

function AuthorBox() {
  return (
    <div className="topic-author" data-testid="topic-author-box">
      <img src="/andreas-hofmarcher-autor.jpg" alt="Andreas Hofmarcher, Gründer von mAIsblau, mit Filmkamera" loading="lazy" />
      <div>
        <span className="topic-author-stand">Stand: November 2026</span>
        <p>
          <strong>Andreas Hofmarcher</strong>, Gründer von mAIsblau – Filmemacher und Journalist aus Wien,
          seit 2014 selbstständig, Ausbildung am Kinsale College (Irland). mAIsblau ist ein
          Ein-Personen-Unternehmen: Andreas produziert jedes Projekt persönlich.
        </p>
        <a href="https://www.linkedin.com/in/andreas-hofmarcher-36062199/" target="_blank" rel="noopener noreferrer" data-testid="topic-author-linkedin">LinkedIn-Profil</a>
      </div>
    </div>
  );
}

function TopicPage({ topic }) {
  useEffect(() => {
    document.title = topic.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', topic.description);
    setCanonical(`${SITE}${topic.path}`);
    window.scrollTo(0, 0);
  }, [topic]);

  return (
    <div className="service-page topic-page" data-testid={`topic-page-${topic.schemaKey}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas[topic.schemaKey]) }} />
      <div className="container">
        <Link to="/" className="sp-back" data-testid="topic-page-back"><ArrowLeft size={16} /> Zur Startseite</Link>
        <h1 className="sp-title">{topic.h1}</h1>
        <p className="topic-teaser" data-testid="topic-teaser"><strong>{topic.teaser}</strong></p>
        {topic.disclaimer && (
          <blockquote className="topic-disclaimer" data-testid="topic-disclaimer">{topic.disclaimer}</blockquote>
        )}
        <AuthorBox />

        <div className="topic-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MdLink }}>
            {topic.body}
          </ReactMarkdown>
        </div>

        <div className="sp-price-box">
          <p className="sp-price">Kostenloses Erstgespräch: +43 681 20179697 · andreas@maisblau.video</p>
          <div className="topic-cta-row">
            <Link to="/#contact" className="sp-cta" data-magnetic="true" data-testid="topic-page-cta">
              Jetzt Projekt anfragen <ArrowRight size={18} />
            </Link>
            <a href="tel:+4368120179697" className="topic-cta-secondary"><Phone size={16} /> Anrufen</a>
            <a href="mailto:andreas@maisblau.video" className="topic-cta-secondary"><Mail size={16} /> E-Mail</a>
          </div>
        </div>

        <div className="sp-others">
          <span>Weiterlesen:</span>
          {topic.related.map((o) => (
            <Link key={o.to} to={o.to}>{o.label}</Link>
          ))}
        </div>

        <div className="sp-footer">
          <span>© 2026 mAIsblau Videoproduktion Wien</span>
          <Link to="/impressum">Impressum</Link>
          <Link to="/datenschutz">Datenschutz</Link>
        </div>
      </div>
    </div>
  );
}

export const topicRoutes = TOPICS.map((t) => ({ path: t.path, element: <TopicPage topic={t} /> }));
